import React, { useEffect } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import {
  Form,
  Field,
  resetForm,
  useWatch,
  WatchField,
  useForm,
  isEmpty
} from '../../../../form'
import MenuItem from '@material-ui/core/MenuItem'
import { Translation, useTranslation } from 'react-i18next'
import CountrySelectField from '../../../common/countrySelectField'
import { withStyles } from '@material-ui/core/styles'
import {
  selectInvoiceCustomer,
  enableCancelInvoice,
  getInvoiceUserEmail,
  getInvoicesStart,
  getFinvoiceOperator,
  getCountryList,
  getUserCompanyUpdateInfo
} from '../../../../actions'
import { invoiceCustomerInfoValidate } from '../../../../containers/validate'
import CompanySearchField from '../../../common/companySearchField'
import { Controller } from 'react-hook-form'
import Button from '@material-ui/core/Button'
import mobileView from '../../../../utils/mobile.utils'

const styles = (theme) => ({
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  selectCustomer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    fontSize: '14px',
    borderBottom: '1px solid gray',
    top: '10px'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  inputLabel: {
    fontSize: '15px'
  },
  label: {
    marginTop: mobileView ? '47px' : ''
  },
  radioButton: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#808080'
  },
  checkbox: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#808080'
  },
  dropDownListsTextField: {
    position: 'relative'
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonNext: {
    padding: !mobileView ? '1px 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  },
  mobileLabel: {
    fontSize: mobileView ? '13px' : '',
    marginBottom: mobileView ? '-60px' : '',
    marginTop: mobileView ? '20px' : '',
    display: !mobileView ? 'none' : '',
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: 500
  }
})

const InvoiceCustomerInfoForm = ({ classes, goNext, formValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { customers, isEdit, countryList, preventCancelInvoice } = useSelector(
    (state) => state.invoice,
    shallowEqual
  )

  const selectedUser = useSelector((state) => state.adminUser.selectedUser)

  // KAS-470 Invoice design changes
  React.useEffect(() => {
    dispatch(getInvoicesStart(selectedUser))
    dispatch(getFinvoiceOperator())
    dispatch(getCountryList())
  }, [selectedUser])

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    validator: (values) => invoiceCustomerInfoValidate(values),
    keepEmptyValuesOnSubmit: true
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    getValues,
    formState: { errors }
  } = form

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    if (values.delivery_method === 'KIRJEPOSTI') {
      values.web_invoice = 'TULOSTUS'
    }
    
    goNext(values)
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  function selectCustomer(e) {
    if (e.target.value >= 0) {
      const _customer = customers
        .filter((el) => e.target.value === el.customer_id)
        .pop()
      dispatch(selectInvoiceCustomer(_customer, getValues()))
    } else {
      dispatch(resetForm('invoice'))
    }
  }

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    if (preventCancelInvoice) {
      dispatch(enableCancelInvoice())
    }
  }, [preventCancelInvoice])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
  }, [])

  const watch = useWatch({
    fields: [
      'company_name',
      'business_id',
      'quick_pay',
      'vat_selection',
      'customer_type'
    ],
    control
  })
  const customer_type = watch.customer_type

  const smallMobileView = window.innerWidth < 400

  return (
    <Form {...form}>
      <div className="row">
        <div className="panel-body" style={{ textAlign: 'center' }}>
          <Controller
            name="is_credit_note"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          <Field
            name="customer_type"
            inputType="radiogroup"
            className={classes.radioButton}
            style={{
              display: smallMobileView ? 'inline-flex' : 'inline-block',
              paddingLeft: smallMobileView && 17
            }}
            // KAS-339 SELECTED_CUSTOMER_TYPE action don't use any more, use WatchField instead
            options={[
              {
                label: t('invoice.company'),
                value: 'company'
              },
              {
                label: t('invoice.private'),
                value: 'private_person'
              }
            ]}
            control={control}
            onChange={(e) => {
              if (e.target.value === 'private_person') {
                setValue('quick_pay', false)
                setValue('vat_selection', 25.5)
              }
            }}
          />
        </div>
        <div
          style={{
            width: mobileView ? '100%' : '330px',
            margin: '0 auto',
            padding: mobileView && '0 30px',
            whiteSpace: mobileView ? 'normal' : 'nowrap'
          }}
        >
          <p className={classes.mobileLabel}>{t('invoice.label')}</p>
          <Field
            name="customer_id"
            label={!mobileView ? t('invoice.label') : null}
            className={classes.selectCustomer}
            inputType="select"
            disabled={isEdit}
            control={control}
            onChange={selectCustomer}
          >
            {_createCustomerMenuItems(customers)}
          </Field>
        </div>
      </div>
      <div className="row">
        <div className="dashboard-content-header">
          <div>
            {customerInfo(
              classes,
              countryList,
              control,
              setValue,
              customer_type,
              watch
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          margin: '40px 10px 15px'
        }}
      >
        <Button
          className={classNames(classes.buttonNext, classes.button)}
          type="submit"
          onClick={() => setAttemptingSubmit(true)}
        >
          {t('asiakkaat.next')}{' '}
          <span
            style={{
              display: 'inline-block',
              transform: 'scaleX(1.5)',
              fontSize: '18px',
              letterSpacing: '-2px'
            }}
          >
            &nbsp; &gt;
          </span>
        </Button>
      </div>
    </Form>
  )
}

const customerInfo = (
  classes,
  countryList,
  control,
  setValue,
  customer_type,
  watch
) => (
  <Translation>
    {(t) => {
      return (
        <div className="panel panel-default" style={{ boxShadow: 'none' }}>
          <h3
            style={{
              textAlign: 'center',
              fontSize: mobileView && '20px',
              fontWeight: '600',
              color: '#000',
              textTransform: 'none'
            }}
          >
            {t('invoice.panelh3')}
          </h3>
          <div
            className="panel-body"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-around'
            }}
          >
            <div
              className="col-xs-12 col-sm-6 col-lg-4"
              style={{ padding: mobileView ? '0 25px' : '' }}
            >
              <div>
                <CountrySelectField
                  t={t}
                  classes={classes}
                  countryList={countryList}
                  control={control}
                  form="invoice"
                  setValue={setValue}
                  watch={watch}
                />
              </div>
              <div className={classes.dropDownListsTextField}>
                <CompanySearchField
                  form="invoice"
                  name="company_name"
                  control={control}
                />
              </div>
              <div>
                <WatchField control={control} fields={'country'}>
                  {(watch) =>
                    watch.country != 1 ? (
                      <WatchField control={control} fields="vat_selection">
                        {(fields) =>
                          fields.vat_selection === 'building_service' ? (
                            <Field
                              name="business_id"
                              inputType="textfield"
                              label={t('asiakkaat.label8')}
                              className={classNames(
                                classes.textField,
                                classes.resize
                              )}
                              control={control}
                            />
                          ) : (
                            // KAS-339 Using watchfield to watch, if customer_type is company, business_id is required
                            <WatchField
                              control={control}
                              fields="customer_type"
                            >
                              {(watch) => (
                                <Field
                                  name="business_id"
                                  inputType="textfield"
                                  label={
                                    watch.customer_type === 'company'
                                      ? t('asiakkaat.label3') + '*'
                                      : t('asiakkaat.label3')
                                  }
                                  className={classNames(
                                    classes.textField,
                                    classes.resize
                                  )}
                                  control={control}
                                />
                              )}
                            </WatchField>
                          )
                        }
                      </WatchField>
                    ) : (
                      // KAS-339 Using watchfield to watch, if customer_type is company, business_id is required
                      // <WatchField control={control} fields="customer_type">
                      //   {(watch) => (
                      <Field
                        name="business_id"
                        inputType="textfield"
                        label={
                          customer_type === 'company'
                            ? t('asiakkaat.label3') + '*'
                            : t('asiakkaat.label3')
                        }
                        className={classNames(
                          classes.textField,
                          classes.resize
                        )}
                        control={control}
                      />
                      //   )}
                      // </WatchField>
                    )
                  }
                </WatchField>
              </div>
            </div>
            <div
              className="col-xs-12 col-sm-6 col-lg-4"
              style={{ padding: mobileView ? '0 25px' : '' }}
            >
              <div>
                <Field
                  name="person_to_contact"
                  inputType="textfield"
                  label={t('asiakkaat.label4')}
                  className={classNames(classes.textField, classes.resize)}
                  control={control}
                />
              </div>
              <div>
                <Field
                  name="person_to_contact_email"
                  inputType="textfield"
                  label={t('asiakkaat.label5')}
                  className={classNames(classes.textField, classes.resize)}
                  control={control}
                />
              </div>
              <div>
                <Field
                  name="person_to_quick_pay_email"
                  inputType="textfield"
                  label={t('asiakkaat.label7')}
                  className={classNames(
                    classes.textField,
                    classes.resize,
                    classes.label
                  )}
                  control={control}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }}
  </Translation>
)

const _createCustomerMenuItems = (customers) => {
  return customers
    .sort((a, b) => a.company_name.localeCompare(b.company_name))
    .map((c) => (
      <MenuItem key={c.customer_id} value={c.customer_id}>
        {c.company_name}
      </MenuItem>
    ))
}

export default withStyles(styles)(InvoiceCustomerInfoForm)
